/* eslint-disable eslint-comments/disable-enable-pair -- needed */
/* eslint-disable no-unused-vars, consistent-return -- needed */
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { ExtendReactlytics } from '@americanexpress/reactlytics';
import {
  getProducts,
  getSelectedProduct,
} from '@americanexpress/product-ducks';

import {
  ComponentLevelNotification,
  Button,
  Checkbox,
  Input,
  Heading,
} from '@americanexpress/dls-react';
import CardMemberInfo from '../../CardMemberInfo';
import { Layout } from '../layout';
import { renderHTML } from '../../../../../utilities/render-html';
import useOneData from '../../../../../hooks/useOneData';
import useDisableButton from '../../../../../hooks/useDisableButton';
import {
  getEndpoint,
  getEnrollmentRequestBodyMilesHeadstart,
} from '../../../../../api/api-utils';
import { CreateLoyaltyBenefitEnrollmentStatusVx } from '../../../../../constants';
import Warning from './components/InputWarning';
import { AuthN } from './components/AuthN';
import { SystemError } from './components/SystemError';

const LoanApply = (props) => {
  const [checkbox, toggleCheckBox] = useState(false);

  const {
    stepTransition,
    selectedProduct,
    callToAction,
    title,
    description,
    faasApiUrl,
    benefitName,
    cardname,
    benefitTitle,
    lastDigits,
    productDescription,
    maxMiles,
    minMiles,
    accountKey,
    cardToken,
    cardData,
    languageData,
  } = props;

  const { isFetching, error, data } = cardData;
  const intl = useIntl(); // Hook for accessing locale content, intl.formatMessage()

  const label = callToAction?.INTERNAL?.label || callToAction?.ANCHOR?.label || 'Enroll Now';
  const locale = props?.locale;
  const safeDescription = renderHTML(description);
  const endpoint = getEndpoint(
    faasApiUrl,
    CreateLoyaltyBenefitEnrollmentStatusVx
  );
  // const requestBody = getEnrollmentRequestBody(selectedProduct, sorBenefitId, { benefitName, cardname });

  const [inputNumber, setInputNumber] = useState(null);
  const [digitsError, setDigitsError] = useState(false);
  const [minimumError, setMinimumError] = useState(false);
  const [leadZeroError, setLeadZeroError] = useState(false);
  const [exceededError, setExceededError] = useState(false);
  const [initialDisable, setInitialDisable] = useState(true);
  const [termsError, setTermsError] = useState(false);
  const [maxPoints, setMaxPoints] = useState(maxMiles);
  const [minPoints, setMinPoints] = useState(minMiles);
  const [cid, displayCID] = useState(false);
  const [systemError, showSystemError] = useState(false);

  const requestBody = getEnrollmentRequestBodyMilesHeadstart(
    selectedProduct,
    'miles-headstart',
    inputNumber,
    locale,
    { benefitName, cardname }
  );
  const endpointOptions = {
    body: requestBody,
    defer: true, // defers fetchye call until run function is invoked
  };
  const { isLoading, run } = useOneData(endpoint, endpointOptions);
  const { isDisabledAfterSubmit, disableButton } = useDisableButton();

  const parseEntities = (txt) => new DOMParser().parseFromString(txt, 'text/html').body.textContent;

  const authComplete = async () => {
    disableButton();
    const { data: enrollmentSuccess, error: enrollmentError } = await run();

    if (enrollmentSuccess) {
      stepTransition(true, false);
    }
    if (enrollmentError) {
      stepTransition(false, true);
    }
  };

  const authReload = () => {
    if (global.BROWSER) {
      window?.location?.reload();
    }
  };

  const authCancel = () => {
    setInitialDisable(true);
    setInputNumber(null);
    toggleCheckBox(false);
    displayCID(false);
  };

  const authDefault = () => {
    showSystemError(true);
  };

  const isDisabled = () => !inputNumber
    || initialDisable
    || exceededError
    || minimumError
    || digitsError
    || leadZeroError
    || termsError
    || isDisabledAfterSubmit;

  const submitClickHandler = async () => {
    if (inputNumber > Number.parseInt(maxPoints, 10)) {
      return setExceededError(true);
    }

    if (inputNumber < Number.parseInt(minPoints, 10)) {
      return setMinimumError(true);
    }

    if (!checkbox) {
      return setTermsError(true);
    }
    setTermsError(false);
    displayCID(true);
  };

  const removeCommasFromNumber = (numberString) => numberString.split(',').join('');

  const formatNumberWithCommas = (numberString) => {
    const arr = [];
    let counter = -1;
    for (let i = numberString.length; i > -1; i -= 1) {
      counter += 1;
      if (counter % 3 === 0) {
        arr.unshift(',', numberString[i]);
      } else {
        arr.unshift(numberString[i]);
      }
    }
    if (arr[0] === ',') {
      arr.shift();
    }
    return arr.join('').slice(0, -1);
  };

  const inputFormChange = (e) => {
    const E = removeCommasFromNumber(e);
    if (!initialDisable && E.length === 0) {
      // dirty form but no input
      setInputNumber(null);
      setDigitsError(false);
      setMinimumError(false);
      setLeadZeroError(false);
      setExceededError(false);
      setInitialDisable(false);
      setTermsError(false);
    } else {
      setInputNumber(E);
      setDigitsError(!/^\d+$/.test(E));
      setLeadZeroError(!/^[1-9]/.test(E));
      setExceededError(Number.parseInt(E, 10) > Number.parseInt(maxPoints, 10));
      setMinimumError(Number.parseInt(E, 10) < Number.parseInt(minPoints, 10));
      setInitialDisable(false);
      setTermsError(false);
    }
  };

  const isInputSuccess = () => inputNumber
    && !initialDisable
    && !exceededError
    && !minimumError
    && !digitsError
    && !leadZeroError;

  const hasWarning = () => exceededError || minimumError || digitsError || leadZeroError;

  const formattedMaxMiles = formatNumberWithCommas(
    Number.parseInt(maxPoints, 10).toString()
  );
  const formattedMinMiles = formatNumberWithCommas(
    Number.parseInt(minPoints, 10).toString()
  );

  const showWarning = () => {
    let values = null;
    let errorType = null;
    if (termsError) {
      errorType = 'termsError';
    }
    if (leadZeroError) {
      errorType = 'leadZeroError';
    }
    if (digitsError) {
      errorType = 'digitsError';
    }
    if (exceededError) {
      values = { amount: maxPoints };
      errorType = 'exceededError';
    }
    if (minimumError) {
      values = { amount: minPoints };
      errorType = 'minimumError';
    }
    if (errorType) {
      return (
        <Warning
          id={errorType}
          values={values}
          maxMiles={formattedMaxMiles}
          minMiles={formattedMinMiles}
        />
      );
    }
    return null;
  };

  const onChange = () => {
    if (!checkbox) {
      toggleCheckBox(true);
    } else {
      toggleCheckBox(false);
    }
  };

  return (
    <>
      <section className="text-justify">
        <Heading level={2} variant="sans-small-regular" className="text-align-center margin-0 margin-3-b pad-0">
          {renderHTML(benefitTitle)}
        </Heading>
        {safeDescription && <article className="margin-3-b">{safeDescription}</article>}
      </section>
      {!cid && !systemError && (
        <Layout>
          {!isFetching && !error && (
            <CardMemberInfo {...data} languageData={languageData} />
          )}
          <span>
            <span>
              <div className="margin-1-t font-weight-bold">
                How many miles would you like to be advanced? (Up to{' '}
                {formattedMaxMiles} miles)
              </div>
              <section className="pad-1-t pad-2-b">
                <Input
                  id="enrollment-input"
                  data-testid="miles-input"
                  onChange={(e) => inputFormChange(e.target.value)}
                  success={isInputSuccess()}
                  status={hasWarning() ? 'error' : 'default'}
                  value={
                    inputNumber ? formatNumberWithCommas(inputNumber) : null
                  }
                />
                {showWarning() && (
                  <ComponentLevelNotification className="pad-1-t">
                    {showWarning()}
                  </ComponentLevelNotification>
                )}
              </section>
            </span>
            {intl.formatMessage({
              id: 'hide-ui.tnc-checkbox',
              defaultMessage: '',
            }) === 'true'
              ? ''
              : (
                <div id="checkboxLabel">
                  <Checkbox
                    id="enrollment-checkbox"
                    reactlytics={{
                      element: `${cardname}>>${benefitName}>>agree-to-terms`,
                    }}
                    label={`${intl.formatMessage({ id: 'shared.terms-links.terms-and-conditions', defaultMessage: 'I understand and agree to the terms and conditions.‡' })}`}
                    onChange={onChange}
                  />
                </div>
              )}
            <ExtendReactlytics
              analytics={{
                event: {
                  meta: {
                    benefitname: `${parseEntities(benefitTitle)?.replace(/ /g, '-').toLowerCase()}`,
                  },
                },
              }}
            >
              <Button
                className="width-full flex flex-column flex-align-items-center margin-auto-lr margin-2-t margin-1-b"
                data-testid="loan-enroll-submit-test"
                reactlytics={{
                  element: `cta>>enroll-submit>>${cardname?.replace(/ /g, '-').toLowerCase()}`,
                  type: 'impression',
                }}
                onClick={submitClickHandler}
                aria-disabled={isDisabled()}
                working={isLoading}
                aria-label={`${`${intl.formatMessage({ id: label })} ${benefitName}`}`}
                variant="primary"
              >
                {label}
              </Button>
            </ExtendReactlytics>
          </span>
        </Layout>
      )}
      {cid && !systemError && (
        <Layout>
          <AuthN
            accountKey={accountKey}
            cardToken={cardToken}
            productDescription={productDescription}
            lastDigits={lastDigits}
            authComplete={authComplete}
            authCancel={authCancel}
            authError={authReload}
            authDefault={authDefault}
          />
        </Layout>
      )}
      {systemError && (
        <SystemError />
      )}
    </>
  );
};

export function mapStateToProps(state) {
  const accountToken = getSelectedProduct(state);
  const selectedProduct = getProducts(state)[accountToken];
  const lastDigits = selectedProduct.account.display_account_number;
  const productDescription = selectedProduct.product.description;
  const accountKey = selectedProduct.account_key;
  const cardToken = selectedProduct.account_token;
  return {
    accountToken,
    lastDigits,
    productDescription,
    accountKey,
    cardToken,
  };
}

export default connect(mapStateToProps, null)(LoanApply);
